import { useState } from "react";
import useSWR from "swr";
import { toast } from "sonner";
import { Trash2, UserPlus } from "lucide-react";
import { fetcher, http, apiError } from "@/lib/api";
import { DataTable, fmtDate } from "@/components/admin/DataTable";

const EMPTY = { name: "", email: "", password: "", role: "staff" };
const field = "h-11 px-4 rounded-full border border-[#D7D4CF] text-sm bg-white focus:outline-none focus:border-[#C9A961]";

function AddMember({ onAdded }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await http.post("/auth/users", f);
      toast.success(`${f.name} added to the team`);
      setF(EMPTY);
      onAdded();
    } catch (e2) {
      toast.error(apiError(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} data-testid="team-add-form" className="rounded-3xl bg-white border border-[#D7D4CF]/70 p-6 mb-6">
      <h3 className="font-editorial text-2xl text-[#1D3A2F] mb-4">Add team member</h3>
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <input data-testid="team-name" required minLength={2} placeholder="Name" value={f.name} onChange={set("name")} className={field} />
        <input data-testid="team-email" required type="email" placeholder="Email" value={f.email} onChange={set("email")} className={field} />
        <input data-testid="team-password" required minLength={8} type="text" placeholder="Password (min 8)" value={f.password} onChange={set("password")} className={field} />
        <select data-testid="team-role" value={f.role} onChange={set("role")} className={field}>
          <option value="staff">Staff (view only)</option>
          <option value="admin">Admin (can manage team)</option>
        </select>
        <button data-testid="team-add-submit" disabled={busy} className="btn-gold h-11 rounded-full text-[11px] tracking-[0.18em] uppercase font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60">
          <UserPlus className="w-4 h-4" /> Add
        </button>
      </div>
      <p className="text-xs text-[#556B57] mt-3">Share the password with your team member privately. Staff can view all records; only admins can add or remove team members.</p>
    </form>
  );
}

export function TeamTab({ me }) {
  const { data, mutate } = useSWR("/auth/users", fetcher);
  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.email}?`)) return;
    try {
      await http.delete(`/auth/users/${u.id}`);
      toast.success("Team member removed");
      mutate();
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  const columns = [
    { label: "Name", key: "name", render: (r) => <span className="font-medium">{r.name}</span> },
    { label: "Email", key: "email" },
    { label: "Role", key: "role", render: (r) => <span className="capitalize">{r.role}</span> },
    { label: "Added", key: "created_at", render: (r) => <span className="text-xs">{fmtDate(r.created_at)}</span> },
    {
      label: "", key: "actions",
      render: (r) => r.id !== me.id && (
        <button onClick={() => remove(r)} data-testid={`team-remove-${r.id}`} className="w-9 h-9 rounded-full border border-[#D7D4CF] inline-flex items-center justify-center hover:border-red-400 hover:text-red-600">
          <Trash2 className="w-4 h-4" />
        </button>
      ),
    },
  ];
  return (
    <div>
      <AddMember onAdded={mutate} />
      <DataTable rows={data} columns={columns} testid="admin-team-table" title="Team" filename="fresio-team" />
    </div>
  );
}
